import { Component, OnInit } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { Invoice } from '../../invoice/invoice.model';
import { SessionStorageService } from '../../../services/session-storage.service';
import {tap} from "rxjs";
import {ActivatedRoute, Router} from "@angular/router";
import {AccountsService} from "../../accounts.service";
import {Receipt} from "../../receipt/receipt.model";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { PermissionService } from '../../../security/permission.service';
import { app_permissions } from '../../../security/app-permissions';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { accountingNumberWidth } from '../../shared/accounting-line-width';

interface ReceiptAllocationRow {
    invoice: Invoice;
    selected: boolean;
    allocatedAmount: number;
    openAmount: number;
    discountAmount?: number;
    writeOffAmount?: number;
    bankChargeAmount?: number;
    withholdingAmount?: number;
    fxDifferenceAmount?: number;
    adjustmentsExpanded?: boolean;
}

@Component({
    selector: 'app-apply-receipt-invoice',
    templateUrl: './apply-receipt-invoice.component.html',
    styleUrls: ['./apply-receipt-invoice.component.css', '../../shared/accounting-apply-layout.css'],
    providers: [MessageService],
    standalone: true,
    imports: [
    CommonModule,
    FormsModule,
    BreadcrumbModule,
    ButtonModule,
    ToastModule,
    ProgressBarModule,
    InputNumberModule,
    CheckboxModule,
    PageShellComponent,
    PageFormSectionComponent
]
})
export class ApplyReceiptInvoiceComponent implements OnInit {
    readonly numberInputWidth = accountingNumberWidth;

    adjustmentsOpen(row: ReceiptAllocationRow): boolean {
        if (row.selected && (this.getRowTotal(row) <= 0 || this.getRowTotal(row) > row.openAmount)) return true;
        return row.adjustmentsExpanded ?? !!(row.discountAmount || row.writeOffAmount || row.bankChargeAmount || row.withholdingAmount || row.fxDifferenceAmount);
    }

    toggleAdjustments(row: ReceiptAllocationRow): void {
        row.adjustmentsExpanded = !this.adjustmentsOpen(row);
    }

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    receiptId!: number;
    editMode = false;
    receipt!: Receipt;

    allocationRows: ReceiptAllocationRow[] = [];
    totalAllocatedAmount: number = 0;
    balanceReceiptAmount: number = 0;
    receiptAmount: number = 0;
    isLoading: boolean = true;

    canApplyReceipt: boolean = false;

    constructor(
        private accountsService: AccountsService,
        private messageService: MessageService,
        private route: ActivatedRoute, private router: Router,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService
    ) {
    }

    ngOnInit(): void {
        this.canApplyReceipt = this.permissionService.hasPermission(app_permissions.accounts.receipt.edit);
        this.breadcrumbItems = [
            { label: 'Accounts', routerLink: '/accounts' },
            { label: 'Receivables', routerLink: '/accounts/enterprise/receivables/to-be-invoiced' },
            { label: 'Generated Receipts', routerLink: '/accounts/enterprise/receivables/generated-receipts' },
            { label: 'Apply Receipt to Invoice' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        if (!this.canApplyReceipt) {
            this.isLoading = false;
            return;
        }

        this.route.params.subscribe(params => {
            this.receiptId = params['receiptId'];
            this.editMode = !!this.receiptId;
            this.loadReceiptData();
        });
    }

    private loadReceiptData() {
        this.accountsService.getReceiptById(this.receiptId).subscribe(value => {
            this.receipt = value;
            this.loadUnpaidInvoices();
            this.receiptAmount = value.openAmount ?? value.amount ?? 0;
            this.balanceReceiptAmount = this.receiptAmount;
        });
    }

    loadUnpaidInvoices() {
		this.isLoading = true;
		const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
		const party = this.receipt.mainParty;
		if (!party?.id?.entityId || !party.id.entityType) {
			this.allocationRows = [];
			this.isLoading = false;
			return;
		}
		this.accountsService.getGeneratedInvoices(0, 100, companyId, undefined, undefined, undefined, undefined, undefined, {
			postingStatuses: ['POSTED'],
			paymentStatuses: ['PARTIALLY_PAID', 'UNPAID'],
			partyId: party.id.entityId,
			partyType: party.id.entityType
		}).pipe(
            tap(res => {
                this.allocationRows = res.items.map(invoice => ({
                    invoice,
                    selected: false,
                    allocatedAmount: 0,
                    openAmount: invoice.openAmount ?? invoice.totalAmount ?? 0,
                    discountAmount: 0,
                    writeOffAmount: 0,
                    bankChargeAmount: 0,
                    withholdingAmount: 0,
                    fxDifferenceAmount: 0
                }));
                this.calculateTotals();
            })
        ).subscribe({
            error: (err) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error loading unpaid invoices'
                });
                this.isLoading = false;
            },
            complete: () => {
                this.isLoading = false;
            }
        });
    }


    onRowSelect(row: ReceiptAllocationRow): void {
        if (row.selected) {
            const remaining = Math.max(0, this.receiptAmount - this.totalAllocatedAmount);
            row.allocatedAmount = Math.min(row.openAmount, remaining);
        } else {
            row.allocatedAmount = 0;
        }
        this.calculateTotals();
    }

    onAllocationChange(row: ReceiptAllocationRow): void {
        row.allocatedAmount = this.nonNegative(row.allocatedAmount);
        row.discountAmount = this.nonNegative(row.discountAmount);
        row.writeOffAmount = this.nonNegative(row.writeOffAmount);
        row.bankChargeAmount = this.nonNegative(row.bankChargeAmount);
        row.withholdingAmount = this.nonNegative(row.withholdingAmount);
        row.fxDifferenceAmount = this.nonNegative(row.fxDifferenceAmount);
        row.selected = this.getRowTotal(row) > 0;
        if (row.discountAmount || row.writeOffAmount || row.bankChargeAmount || row.withholdingAmount || row.fxDifferenceAmount) row.adjustmentsExpanded = true;
        this.calculateTotals();
    }

    calculateTotals(): void {
        this.totalAllocatedAmount = this.allocationRows
            .filter(row => row.selected)
            .reduce((sum, row) => sum + this.getRowTotal(row), 0);
        this.balanceReceiptAmount = Math.max(0, this.receiptAmount - this.totalAllocatedAmount);
    }

    hasValidationError(): boolean {
        return this.totalAllocatedAmount > this.receiptAmount
            || this.allocationRows.some(row => row.selected && (this.getRowTotal(row) <= 0 || this.getRowTotal(row) > row.openAmount));
    }

    getRowTotal(row: ReceiptAllocationRow): number {
        return (row.allocatedAmount || 0)
            + (row.discountAmount || 0)
            + (row.writeOffAmount || 0)
            + (row.bankChargeAmount || 0)
            + (row.withholdingAmount || 0)
            + (row.fxDifferenceAmount || 0);
    }

    getRowRemaining(row: ReceiptAllocationRow): number {
        return Math.max(0, row.openAmount - this.getRowTotal(row));
    }

    private nonNegative(value?: number): number {
        return Math.max(0, Number(value || 0));
    }


    applyReceipt() {
        if (!this.canApplyReceipt) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Permission',
                detail: 'You do not have permission to apply receipts.'
            });
            return;
        }
        const allocations = this.allocationRows
            .filter(row => row.selected && this.getRowTotal(row) > 0)
            .map(row => ({
                invoiceId: row.invoice.invoiceId!,
                allocatedAmount: row.allocatedAmount,
                discountAmount: row.discountAmount,
                writeOffAmount: row.writeOffAmount,
                bankChargeAmount: row.bankChargeAmount,
                withholdingAmount: row.withholdingAmount,
                fxDifferenceAmount: row.fxDifferenceAmount
            }));
        if(allocations.length == 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Please, select at least one invoice.'
            });
            return;
        }
        if (this.hasValidationError()) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Validation',
                detail: 'Allocation amounts must be positive and cannot exceed invoice or receipt open amounts.'
            });
            return;
        }
        this.isLoading = true;
        this.accountsService.applyReceiptAllocations(this.receiptId, { allocations }).subscribe(value => {
            this.isLoading = false;
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'The invoices has been applied successfully.'
            });
            setTimeout(() => {
                this.router.navigate(['/accounts/enterprise/receivables/generated-receipts']);
            }, 3000);
        });
    }
}
